import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { ActionButton } from "@via/components/action-button/action-button";
import Drawer from "@via/components/drawer";
import { GenericError } from "@via/components/error";
import Table, { TableSize } from "@via/components/table/table";
import { BUTTONS, CUSTOM_INSTRUCTION_TABLE, ERROR } from "@via/utils/constants";
import { formatDateString } from "@via/utils/helpers";
import { useState } from "react";
import { useCustomInstructions } from "@via/pages/account/profile/integration-tokens/custom-instructions/custom-instructions.api";
import CreateCustomInstructionForm from "@via/pages/account/profile/integration-tokens/custom-instructions/components/create-custom-instruction-form";
import DeleteCustomInstructionModal from "@via/pages/account/profile/integration-tokens/custom-instructions/components/delete-custom-instruction-modal";
import EditCustomInstructionForm from "@via/pages/account/profile/integration-tokens/custom-instructions/components/edit-custom-instruction-form";
import TableContextMenu from "@via/components/table/table-context-menu";
import { TableContextMenuInterface } from "@via/utils/interfaces";
import { AIInstruction } from "common";
import CustomInstructionFilter from "@via/pages/account/profile/integration-tokens/custom-instructions/components/custom-instruction-filter";
import { TruncatedText } from "@via/components/truncated-text";

const columnHelper = createColumnHelper<AIInstruction>();

const defaultParams = {
  filters: [],
  page: 0,
  pageSize: 10,
};

const CustomInstructions = () => {
  const [customInstructionToBeDeleted, setCustomInstructionToBeDeleted] =
    useState<AIInstruction | null>(null);
  const [customInstructionToBeEdited, setCustomInstructionToBeEdited] =
    useState<AIInstruction | null>(null);
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [page, setPage] = useState(defaultParams.page);
  const [pageSize, setPageSize] = useState(defaultParams.pageSize);

  const [columnFilters, setColumnFilters] = useState<
    Array<{ id: string; value: unknown }>
  >(defaultParams.filters);

  const { data, isLoading, isError } = useCustomInstructions(
    page,
    pageSize,
    columnFilters
  );

  const handleAction = (option: TableContextMenuInterface, row: any): void => {
    switch (option.name) {
      case "Delete":
        setCustomInstructionToBeDeleted(row);
        break;
      case "Edit":
        setCustomInstructionToBeEdited(row);
        setIsEditDrawerOpen(true);
        break;
      default:
        break;
    }
  };

  const columns = [
    columnHelper.accessor("id", {
      cell: info => info.getValue(),
      header: () => <span>Id</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("name", {
      cell: info => info.getValue(),
      header: () => <span>Instruction Name</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("instruction", {
      cell: info => (
        <TruncatedText
          text={info.getValue() as string}
          maxLength={50}
          id={`tooltip-${info.column.id}-${info.row.original.name}`}
        />
      ),
      header: () => <span>Instruction</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("createdAt", {
      cell: info => {
        const value = info.getValue();
        return <span>{formatDateString(value, "LLL")}</span>;
      },
      enableSorting: false,
      header: () => <span>Created At</span>,
    }),
    columnHelper.accessor("id", {
      cell: info => {
        return (
          <TableContextMenu
            options={CUSTOM_INSTRUCTION_TABLE}
            onSelect={option => handleAction(option, info.row.original)}
          />
        );
      },
      header: () => <span>Options</span>,
      enableSorting: false,
    }),
  ] as Array<ColumnDef<AIInstruction>>;

  return (
    <div className="mt-6 p-6 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:w-full sm:rounded-lg">
      <div className="via-page-heading-wrapper">
        <h2 className="via-h2">Custom Instructions</h2>
        <ActionButton
          button={BUTTONS.CREATE_INSTRUCTION}
          onClick={() => {
            setIsCreateDrawerOpen(true);
          }}
        />
      </div>
      {isError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <Table
          isLoading={isLoading}
          data={data?.data}
          columns={columns}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          total={data?.total}
          size={TableSize.Big}
          additionalFilters={
            <CustomInstructionFilter
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              setPage={setPage}
              setPageSize={setPageSize}
            />
          }
        />
      )}
      {customInstructionToBeDeleted && (
        <DeleteCustomInstructionModal
          customInstruction={customInstructionToBeDeleted}
          setCustomInstructionToBeDeleted={() => {
            setCustomInstructionToBeDeleted(null);
          }}
        />
      )}
      <Drawer
        id="create-custom-instruction"
        open={isCreateDrawerOpen}
        setOpen={setIsCreateDrawerOpen}>
        <CreateCustomInstructionForm setOpen={setIsCreateDrawerOpen} />
      </Drawer>
      <Drawer
        id="edit-custom-instruction"
        open={isEditDrawerOpen}
        setOpen={setIsEditDrawerOpen}>
        <EditCustomInstructionForm
          setOpen={setIsEditDrawerOpen}
          customInstruction={customInstructionToBeEdited}
        />
      </Drawer>
    </div>
  );
};

export default CustomInstructions;
